/**
 * Aufbereitung fuer den App-Tab „MCP" (/#/mcp), bewusst ohne DOM.
 *
 * Das Frontend-Vitest laeuft mit environment: 'node', testbare Logik gehoert
 * deshalb hierher und nicht in die View. Vorlage: mcp-oauth-flow.ts der Kaufland-App.
 * Datenquelle ist die RPC `otto_mcp_status()`.
 */

/** Feste Server-URL fuer den KI-Client, ueber die App-Domain (nginx reicht an die Function weiter). */
export const MCP_SERVER_URL = 'https://otto.goe-connect.de/mcp';

export interface McpZugang {
  id: string;
  name: string;
  prefix: string;
  environment: string | null;
  created_at: string;
  last_used_at: string | null;
}

export interface McpVerbindung {
  id: string;
  client_name: string | null;
  scope: string;
  connected_at: string;
  refreshed_at: string | null;
  refresh_expires_at: string | null;
}

export interface McpStatus {
  zugang: McpZugang | null;
  verbindungen: McpVerbindung[];
}

export interface FormattedZugang {
  vorhanden: boolean;
  zustandText: string;
  erstelltText: string;
  letzteNutzungText: string;
  row: McpZugang | null;
}

export interface FormattedVerbindung extends McpVerbindung {
  anzeigeName: string;
  scopeText: string;
  verbundenText: string;
  zuletztText: string;
  abgelaufen: boolean;
  zustandText: string;
}

/** Datum menschenlesbar, ohne bei Muell NaN zu zeigen. */
export function datumText(iso: string | null | undefined): string {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('de-DE');
}

/** Antwort der RPC tolerant lesen: fehlende Felder statt Absturz. */
export function parseMcpStatus(data: unknown): McpStatus {
  const roh = (Array.isArray(data) ? data[0] : data) as Partial<McpStatus> | null | undefined;
  return {
    zugang: roh?.zugang ?? null,
    verbindungen: Array.isArray(roh?.verbindungen) ? roh!.verbindungen : [],
  };
}

export function formatZugang(row: McpZugang | null): FormattedZugang {
  if (!row) {
    return {
      vorhanden: false,
      zustandText: 'Noch kein Zugang angelegt.',
      erstelltText: '-',
      letzteNutzungText: '-',
      row: null,
    };
  }
  return {
    vorhanden: true,
    zustandText: 'Aktiv',
    erstelltText: datumText(row.created_at),
    letzteNutzungText: row.last_used_at ? datumText(row.last_used_at) : 'noch nie',
    row,
  };
}

/** Deutsche Beschriftung des Umfangs. Unbekanntes wird unveraendert durchgereicht. */
export function scopeLabel(scope: string): string {
  const teile = scope.split(/\s+/).filter((t) => t !== '');
  if (teile.length === 0) return scope;
  if (!teile.every((t) => t === 'otto:read' || t === 'otto:write' || t === 'otto:publish')) return scope;
  if (teile.includes('otto:publish') && teile.includes('otto:write')) return 'Lesen, ändern und an OTTO senden';
  if (teile.includes('otto:write')) return 'Lesen und ändern';
  return 'Nur lesen';
}

export function formatVerbindungen(rows: McpVerbindung[], jetzt: number = Date.now()): FormattedVerbindung[] {
  return [...rows]
    .sort((a, b) => new Date(b.connected_at).getTime() - new Date(a.connected_at).getTime())
    .map((r) => {
      const ablauf = r.refresh_expires_at ? new Date(r.refresh_expires_at).getTime() : NaN;
      const abgelaufen = !Number.isNaN(ablauf) && ablauf < jetzt;
      return {
        ...r,
        anzeigeName:
          r.client_name && r.client_name.trim() !== '' ? r.client_name : 'Unbenannter Client',
        scopeText: scopeLabel(r.scope),
        verbundenText: datumText(r.connected_at),
        zuletztText: datumText(r.refreshed_at ?? r.connected_at),
        abgelaufen,
        zustandText: abgelaufen ? 'Abgelaufen' : 'Aktiv',
      };
    });
}

/** Gleiche Grenzen wie die RPC (1 bis 60 Zeichen), damit der Fehler im Formular auffaellt. */
export function validateZugangName(name: string): string | null {
  const n = name.trim();
  if (n === '') return 'Bitte einen Namen eingeben.';
  if (n.length > 60) return 'Der Name darf höchstens 60 Zeichen haben.';
  return null;
}

/** Fehlertext der Anlage-RPC in einen Satz fuer den Haendler uebersetzen. */
export function anlageFehlerText(message: string): string {
  if (message.includes('es gibt bereits einen aktiven MCP-Zugang')) {
    return 'Es gibt bereits einen aktiven Zugang. Widerrufe ihn zuerst, um einen neuen zu erzeugen.';
  }
  return `Fehlgeschlagen: ${message}`;
}
